import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Building2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { Provider } from "@/types";
import { useAuthStore } from "@/store/auth";
import { SPAIN_COMUNIDADES, SPAIN_PROVINCES_BY_COMUNIDAD } from "@/lib/spainGeo";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { PasswordInput } from "@/components/ui/PasswordInput";
import { Card } from "@/components/ui/Card";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";

const NEW_PROVIDER_VALUE = "__new__";

const selectClass =
  "rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20";

const clubSchema = z
  .object({
    nombre: z.string().min(2, "Mínimo 2 caracteres"),
    direccion: z.string().optional(),
    pais: z.string().optional(),
    comunidadAutonoma: z.string().optional(),
    provincia: z.string().optional(),
    localidad: z.string().optional(),
    descripcion: z.string().optional(),
    imagenUrl: z.string().optional(),
    telefono: z.string().optional(),
    email: z.string().optional(),
    providerId: z.string().optional(),
    newProviderNombre: z.string().optional(),
    newProviderApellidos: z.string().optional(),
    newProviderEmail: z.string().optional(),
    newProviderPassword: z.string().optional(),
    newProviderConfirmPassword: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.providerId !== NEW_PROVIDER_VALUE) return;
    if (!data.newProviderNombre || data.newProviderNombre.length < 2) {
      ctx.addIssue({ code: "custom", path: ["newProviderNombre"], message: "Mínimo 2 caracteres" });
    }
    if (!data.newProviderEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.newProviderEmail)) {
      ctx.addIssue({ code: "custom", path: ["newProviderEmail"], message: "Email inválido" });
    }
    if (!data.newProviderPassword || data.newProviderPassword.length < 8) {
      ctx.addIssue({ code: "custom", path: ["newProviderPassword"], message: "Mínimo 8 caracteres" });
    }
    if (data.newProviderPassword !== data.newProviderConfirmPassword) {
      ctx.addIssue({ code: "custom", path: ["newProviderConfirmPassword"], message: "Las contraseñas no coinciden" });
    }
  });
type ClubForm = z.infer<typeof clubSchema>;

export function ClubCreatePage() {
  const isSuperAdmin = useAuthStore((s) => s.user?.role === "SUPER_ADMIN");
  const navigate = useNavigate();

  const { data: providers } = useQuery({
    queryKey: ["providers"],
    queryFn: async () => (await api.get<{ data: Provider[] }>("/providers?limit=100")).data.data,
    enabled: isSuperAdmin,
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ClubForm>({ resolver: zodResolver(clubSchema), defaultValues: { pais: "España" } });

  const selectedProviderId = watch("providerId");
  const comunidadSeleccionada = watch("comunidadAutonoma");

  const saveClub = useMutation({
    mutationFn: (data: ClubForm) => {
      const payload: Record<string, unknown> = {
        nombre: data.nombre,
        direccion: data.direccion || null,
        pais: data.pais || null,
        comunidadAutonoma: data.comunidadAutonoma || null,
        provincia: data.provincia || null,
        localidad: data.localidad || null,
        descripcion: data.descripcion || null,
        imagenUrl: data.imagenUrl || null,
        telefono: data.telefono || null,
        email: data.email || null,
      };
      if (isSuperAdmin) {
        if (data.providerId === NEW_PROVIDER_VALUE) {
          payload.newProvider = {
            nombre: data.newProviderNombre,
            apellidos: data.newProviderApellidos || null,
            email: data.newProviderEmail,
            password: data.newProviderPassword,
          };
        } else if (data.providerId) {
          payload.providerId = Number(data.providerId);
        }
      }
      return api.post<{ id: number }>("/clubs", payload);
    },
    onSuccess: ({ data }) => {
      toast.success("Club creado correctamente");
      navigate(`/clubes/${data.id}`);
    },
    onError: (error: any) => toast.error(error?.response?.data?.error ?? "No se pudo crear el club"),
  });

  return (
    <div className="flex flex-col gap-8">
      <Breadcrumbs items={[{ label: "Clubes", to: "/clubes" }, { label: "Nuevo club" }]} />

      <div className="flex items-center gap-4">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
          <Building2 className="h-6 w-6" />
        </span>
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Nuevo club</h1>
          <p className="text-ink-400">Crea un club nuevo en la plataforma.</p>
        </div>
      </div>

      <Card>
        <form
          onSubmit={handleSubmit((data) => {
            if (isSuperAdmin && !data.providerId) {
              toast.error("Selecciona un proveedor o crea uno nuevo para este club");
              return;
            }
            saveClub.mutate(data);
          })}
          className="flex flex-col gap-4"
        >
          <Input id="nombre-club" label="Nombre del club" error={errors.nombre?.message} {...register("nombre")} />

          {isSuperAdmin && (
            <div className="flex flex-col gap-3 rounded-xl bg-ink-50/60 p-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="provider-select" className="text-sm font-medium text-ink-600">
                  Proveedor
                </label>
                <select
                  id="provider-select"
                  className="rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                  {...register("providerId")}
                >
                  <option value="">Selecciona un proveedor</option>
                  {providers?.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre} ({p.clubs.length} club{p.clubs.length === 1 ? "" : "es"})
                    </option>
                  ))}
                  <option value={NEW_PROVIDER_VALUE}>+ Proveedor nuevo (club suelto, sin proveedor todavía)</option>
                </select>
              </div>

              {selectedProviderId === NEW_PROVIDER_VALUE && (
                <div className="flex flex-col gap-3 border-t border-ink-100 pt-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      id="new-provider-nombre"
                      label="Nombre de contacto"
                      error={errors.newProviderNombre?.message}
                      {...register("newProviderNombre")}
                    />
                    <Input id="new-provider-apellidos" label="Apellidos de contacto" {...register("newProviderApellidos")} />
                  </div>
                  <Input
                    id="new-provider-email"
                    type="email"
                    label="Email del proveedor"
                    error={errors.newProviderEmail?.message}
                    {...register("newProviderEmail")}
                  />
                  <PasswordInput
                    id="new-provider-password"
                    label="Contraseña"
                    error={errors.newProviderPassword?.message}
                    {...register("newProviderPassword")}
                  />
                  <PasswordInput
                    id="new-provider-confirm-password"
                    label="Confirmar contraseña"
                    error={errors.newProviderConfirmPassword?.message}
                    {...register("newProviderConfirmPassword")}
                  />
                </div>
              )}
            </div>
          )}

          <Input id="direccion-club" label="Dirección" placeholder="C/ Esquiladores, 237" {...register("direccion")} />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Input id="pais-club" label="País" placeholder="España" {...register("pais")} />
            <div className="flex flex-col gap-1.5">
              <label htmlFor="comunidad-club" className="text-sm font-medium text-ink-600">
                Comunidad autónoma
              </label>
              <select
                id="comunidad-club"
                className={selectClass}
                {...register("comunidadAutonoma", {
                  onChange: () => setValue("provincia", ""),
                })}
              >
                <option value="">Selecciona…</option>
                {SPAIN_COMUNIDADES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="provincia-club" className="text-sm font-medium text-ink-600">
                Provincia
              </label>
              <select id="provincia-club" className={selectClass} disabled={!comunidadSeleccionada} {...register("provincia")}>
                <option value="">Selecciona…</option>
                {(SPAIN_PROVINCES_BY_COMUNIDAD[comunidadSeleccionada ?? ""] ?? []).map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <Input id="localidad-club" label="Localidad" placeholder="Cáceres" {...register("localidad")} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input id="telefono-club" label="Teléfono de contacto" placeholder="600 000 000" {...register("telefono")} />
            <Input id="email-club" label="Email de contacto" type="email" placeholder="club@email.com" {...register("email")} />
          </div>
          <Input id="imagen-club" label="URL de imagen" placeholder="https://…" {...register("imagenUrl")} />
          <div className="flex flex-col gap-1.5">
            <label htmlFor="descripcion-club" className="text-sm font-medium text-ink-600">
              Descripción
            </label>
            <textarea
              id="descripcion-club"
              rows={3}
              className="rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
              {...register("descripcion")}
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button type="button" onClick={() => navigate("/clubes")}>
              Cancelar
            </Button>
            <Button type="submit" disabled={saveClub.isPending}>
              Crear club
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
